import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import { Button, Modal } from 'react-bootstrap'
import callAPI from '../../../helpers/callAPI'
import ScheduleCancellation from '../ScheduleCancellation/ScheduleCancellation'
import CloneCampaignContent from './CloneCampaign/CloneCampaignContent'
import moment from 'moment'
import FraudWatchModal from '../../../components/FraudWatchModal/FraudWatchModal'
import { useAuth, useProtectedContent } from '../../../context/AuthContext'
import ScheduleRelaunchModal from '../ScheduleRelaunchModal/ScheduleRelaunchModal'
import DocuSignInsertionOrderContent from '../../../components/CampaignSetupContent/DocusignInsertionOrderContent'
import CampaignSetupContent from '../../../components/CampaignSetupContent/CampaignSetupContent'
import useCallApi from '../../../hooks/useCallApi'

const CampaignNavbar = ({ campaignId, campaignData, showSystemMessage, refetchParent = () => { return } }) => {
    /*
        PROPS
            campaignData: this is optional. If it's present, we will not make an API request to get campaign data since we already have it from the parent.
    */

    const auth = useAuth()
    const showEditCampaign = useProtectedContent(auth.permissions, 'campaign_settings')
    const showAddLeads = useProtectedContent(auth.permissions, 'add_leads')
    const showAddAvatarLeads = useProtectedContent(auth.permissions, 'avatar_add_leads')
    const showCloneCampaign = useProtectedContent(auth.permissions, 'campaign_clone')
    const showFilterOverrides = useProtectedContent(auth.permissions, 'affiliate_filter_overrides')
    const showCampaignApprovals = useProtectedContent(auth.permissions, 'campaign_approvals')
    const showDirectLinkSettings = useProtectedContent(auth.permissions, 'direct_link_settings')
    const showDocusignIO = useProtectedContent(auth.permissions, 'docusign_io')

    const [statsOpen, setStatsOpen] = useState(false)
    const [campaignOpen, setCampaignOpen] = useState(false)
    const [complianceOpen, setComplianceOpen] = useState(false)
    const [mobileOpen, setMobileOpen] = useState(false)
    const [affiliateSettingsOpen, setAffiliateSettingsOpen] = useState(false)
    const [trafficOpen, setTrafficOpen] = useState(false)
    const [dataLoaded, setDataLoaded] = useState(false)
    const [data, setData] = useState(campaignData ? campaignData : {})

    const [showDocusignModal, setShowDocusignModal] = useState(false)
    const [showScheduleCancellationModal, setShowScheduleCancellationModal] = useState(false)
    const [cloneCampaignModalIsOpen, setCloneCampaignModalIsOpen] = useState(false)
    const [showScheduleRelaunchModal, setShowScheduleRelaunchModal] = useState(false)
    const [showCampaignSetupModal, setShowCampaignSetupModal] = useState(false)
    const [campaignPhaseData, setCampaignPhaseData] = useState(null)
    const [reportData, setReportData] = useState(null)
    const { data: otherData, dataLoaded: otherDataLoaded } = useCallApi(`campaign/${campaignId}/otherIO`, 'GET', {}, [campaignId]);

    const [modalData, setModalData] = useState({
        isOpen: false,
        isCreate: false,
    })

    const [fraudWatch, setFraudWatch] = useState({
        id: '',
        campaign: {
            id: campaignId,
            name: data.campaign ? data.campaign.name : '',
            status: '',
        },
        reason: '',
        frequency: '',
        frequency_start_date: moment().format('YYYY-MM-DD'),
        notes: '',
    })

    const [refetch, setRefetch] = useState(false)
    const [isContractAvailable, setIsContractAvailable] = useState(false);

    const checkContractAvailability = async () => {
        try {
            const mbToken = localStorage.getItem('adm-auth-token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/docusignConnect/${campaignId}/downloadInsertionOrder`, {
                method: 'HEAD',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'x-app-token': mbToken,
                }
            });
            setIsContractAvailable(response.status === 200);
        } catch (error) {
            setIsContractAvailable(false);
        }
    };

    useEffect(async () => {
        if (campaignId > 0 && !campaignData) {
            const mbToken = localStorage.getItem('adm-auth-token')
            if (mbToken) {
                let result = await callAPI(`campaign/${campaignId}`, 'GET')

                if (result.success) {
                    setData(result)
                    setFraudWatch({ ...fraudWatch, campaign: { ...fraudWatch.campaign, name: result.campaign.name } })
                }

                // Fetch worksheet report for phase data
                try {
                    const reportResult = await callAPI('campaigns/worksheetReport?ph=All', 'GET')
                    if (reportResult.success) {
                        const phaseData = reportResult.data.find(r => r.offer_id == campaignId) // Use == instead of ===
                        if (phaseData) {
                            setReportData(phaseData)
                        }
                    }
                } catch (error) {
                    console.log('Error fetching report data:', error)
                }

                setDataLoaded(true)
            }
        } else if (campaignData.campaign) {
            setDataLoaded(true)
        }
    }, [campaignId, campaignData, refetch])

    useEffect(() => {
        if (campaignId > 0) {
            checkContractAvailability();
        }
    }, [campaignId]);

    const handleDocumentDownload = async () => {
        try {

            const mbToken = localStorage.getItem('adm-auth-token')

            const response = await fetch(`${process.env.REACT_APP_API_URL}/docusignConnect/${campaignId}/downloadInsertionOrder`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'x-app-token': mbToken,
                }
            });

            if (response.status !== 200) {
                showSystemMessage('error', 'Signed IO does not exist.');
                return;
            }

            const campaignResponse = await callAPI(`campaign/${campaignId}`, 'GET')

            let blob = await response.blob()

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${campaignResponse.campaign.advertiser.name} - ${campaignResponse.campaign.name} - Insertion Order.pdf`; // This will override the Content-Disposition name
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error downloading document:', error);
            showSystemMessage('Error downloading document', 'error');
        }
    };

    return (
        <>
            <Modal show={showDocusignModal} onHide={() => setShowDocusignModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Send Insertion Order Contract</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* <DocuSignContent templateId = 'dfda10b0-1b91-4522-9a15-88e9fe4ff7cf' /> */}
                    <DocuSignInsertionOrderContent offerId={campaignId} closeModal={() => setShowDocusignModal(false)} />
                </Modal.Body>
            </Modal>
            <Modal show={showCampaignSetupModal} onHide={() => setShowCampaignSetupModal(false)} size="xl">
                <CampaignSetupContent modal refetch={() => setRefetch(!refetch)} campaignPhaseData={reportData} closeModal={() => setShowCampaignSetupModal(false)} />
            </Modal>
            {showScheduleRelaunchModal && <ScheduleRelaunchModal refetch={refetchParent} isOpen={showScheduleRelaunchModal} handleClose={() => setShowScheduleRelaunchModal(false)} campaignId={campaignId} />}
            {showScheduleCancellationModal && <Modal show={showScheduleCancellationModal} size="md" onHide={() => setShowScheduleCancellationModal(false)}>
                <ScheduleCancellation refetch={refetchParent} name={dataLoaded ? data.campaign.name : ''} campaignId={campaignId} setShowScheduleCancellationModal={setShowScheduleCancellationModal} showSystemMessage={showSystemMessage} redirects={dataLoaded ? data.campaign.redirects : {}} />
            </Modal>}
            {showCloneCampaign && (
                <Modal show={cloneCampaignModalIsOpen} size="lg" onHide={() => setCloneCampaignModalIsOpen(false)}>
                    <CloneCampaignContent showSystemMessage={showSystemMessage} campaignId={campaignId} setCloneCampaignModalIsOpen={setCloneCampaignModalIsOpen} />
                </Modal>
            )}
            <FraudWatchModal isCampaign refetch={() => setRefetch(!refetch)} modalData={modalData} setModalData={setModalData} showSystemMessage={showSystemMessage} />
            <nav className="mb-header-nav">
                <ul>
                    <li style={{ zIndex: 999 }}>
                        <Dropdown isOpen={campaignOpen} toggle={() => setCampaignOpen(!campaignOpen)} direction="down">
                            <DropdownToggle tag="a" data-toggle="dropdown" aria-expanded={campaignOpen} className="nav-link dropdown-toggle dropdown-toggle-blue dropdown-toggle-right text-white">
                                Campaign
                            </DropdownToggle>
                            <DropdownMenu>
                                <Link className="dropdown-item" to={`/campaign/${campaignId}`}>
                                    Information
                                </Link>
                                {showEditCampaign && (
                                    <>
                                        <DropdownItem divider className="m-0" />
                                        <Link className="dropdown-item" to={`/campaign/${campaignId}/settings`}>
                                            Settings
                                        </Link>
                                    </>
                                )}
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/campaign/${campaignId}/creatives`}>
                                    Creatives
                                </Link>
                                {showCloneCampaign && (
                                    <>
                                        <DropdownItem divider className="m-0" />
                                        <button className="dropdown-item" onClick={() => setCloneCampaignModalIsOpen(true)}>
                                            Clone
                                        </button>
                                    </>
                                )}
                                {dataLoaded && (data.campaign.status.name === 'Active' || data.campaign.status.name === 'Hidden' || data.campaign.status.name === 'By Request') && (
                                    <>
                                        <DropdownItem divider className="m-0" />
                                        <Link
                                            //  to={`/advertiser/${advertiserId}/campaignSetup?id=${item.offer_id}`}
                                            to={'/'}
                                            className="dropdown-item"
                                            onClick={(e) => {
                                                e.preventDefault()
                                                setShowScheduleCancellationModal(true)
                                            }}
                                        >
                                            Schedule Cancellation
                                        </Link>
                                    </>
                                )}

                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/`} onClick={(e) => {
                                    e.preventDefault()
                                    setShowScheduleRelaunchModal(true)
                                }}>
                                    Schedule Relaunch
                                </Link>

                                {showDocusignIO && (
                                    <>
                                        <DropdownItem divider className="m-0" />
                                        <Link className="dropdown-item" onClick={(e) => {
                                            e.preventDefault()
                                            setShowDocusignModal(true)
                                        }} to={`/`}>
                                            Send Insertion Order Contract
                                        </Link>

                                        {isContractAvailable && (
                                            <>
                                                <DropdownItem divider className="m-0" />
                                                <a
                                                    className="dropdown-item"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleDocumentDownload();
                                                    }}
                                                    to={`/`}
                                                >
                                                    Linked Docusign IO
                                                </a>
                                            </>
                                        )}
                                        {(otherDataLoaded && otherData.success && otherData.data.length > 0) && (
                                            <>
                                                <DropdownItem divider className="m-0" />
                                                <Link className="dropdown-item" to={`/campaign/${campaignId}`}>
                                                    Other IO
                                                </Link>
                                            </>
                                        )}
                                    </>
                                )}



                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/advertiser/${dataLoaded && data.campaign.advertiser.id}`}>
                                    View Advertiser
                                </Link>
                                {dataLoaded && reportData && reportData.phase_name && reportData.phase_name !== 'Complete' && (
                                    <>
                                        <DropdownItem divider className="m-0" />
                                        <Link
                                            className="dropdown-item"
                                            to={`/advertiser/${data.campaign.advertiser.id}/campaignSetup?id=${campaignId}`}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                setShowCampaignSetupModal(true)
                                            }}
                                        >
                                            Campaign Setup
                                        </Link>
                                    </>
                                )}
                            </DropdownMenu>
                        </Dropdown>
                    </li>
                    <li style={{ zIndex: 999 }}>
                        <Dropdown isOpen={affiliateSettingsOpen} toggle={() => setAffiliateSettingsOpen(!affiliateSettingsOpen)} direction="down">
                            <DropdownToggle tag="a" data-toggle="dropdown" aria-expanded={affiliateSettingsOpen} className="nav-link dropdown-toggle dropdown-toggle-blue dropdown-toggle-right text-white">
                                Affiliates
                            </DropdownToggle>
                            <DropdownMenu>
                                {showCampaignApprovals &&
                                    <>
                                        <Link className="dropdown-item" to={`/campaign/${campaignId}/campaignApprovals`}>
                                            Campaign Approvals
                                        </Link>
                                        <DropdownItem divider className="m-0" />
                                    </>
                                }
                                {showDirectLinkSettings &&
                                    <>
                                        <Link className="dropdown-item" to={`/directLinkSettings?campaignId=${campaignId}`}>
                                            Direct Link Settings
                                        </Link>
                                        <DropdownItem divider className="m-0" />
                                    </>
                                }
                                <Link className="dropdown-item" to={`/campaign/${campaignId}/callbacks`}>
                                    Callbacks
                                </Link>
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/campaign/${campaignId}/paymentDelayOverrides`}>
                                    Payment Delay Overrides
                                </Link>
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/campaign/${campaignId}/specialRates`}>
                                    Special Rates
                                </Link>
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/campaign/${campaignId}/campaignCaps`}>
                                    Daily Caps
                                </Link>
                                {showAddLeads && (
                                    <>
                                        <DropdownItem divider className="m-0" />
                                        <Link className="dropdown-item" to={`/campaign/${campaignId}/addLeads`}>
                                            Add Leads
                                        </Link>
                                    </>
                                )}
                                {dataLoaded && data.campaign.type.name === 'Avatar' && showAddAvatarLeads && (
                                    <>
                                        <DropdownItem divider className="m-0" />
                                        <Link className="dropdown-item" to={`/campaign/${campaignId}/addLeadsAvatar`}>
                                            Add Leads (Avatar)
                                        </Link>
                                    </>
                                )}
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/campaign/${campaignId}/campaignRequests`}>
                                    Campaign Requests
                                </Link>
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/campaign/${campaignId}/campaignViews`}>
                                    Campaign Views
                                </Link>
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/campaign/${campaignId}/campaignCountryOverrides`}>
                                    Country Blacklist
                                </Link>
                                {showFilterOverrides && (
                                    <>
                                        <DropdownItem divider className="m-0" />
                                        <Link className="dropdown-item" to={`/campaign/${campaignId}/filterOverrides`}>
                                            Link Filter Overrides
                                        </Link>
                                    </>
                                )}
                                <DropdownItem divider className="m-0" />
                                {dataLoaded && <Link className="dropdown-item" to={`/affiliates/byCategory?campaignId=${campaignId}&categoryId=${data.campaign.category.category.id}&subCategoryId=${data.campaign.category.subcategory.id}`}>
                                    Affiliates By Category
                                </Link>}
                            </DropdownMenu>
                        </Dropdown>
                    </li>
                    <li style={{ zIndex: 999 }}>
                        <Dropdown isOpen={statsOpen} toggle={() => setStatsOpen(!statsOpen)} direction="down">
                            <DropdownToggle tag="a" data-toggle="dropdown" aria-expanded={statsOpen} className="nav-link dropdown-toggle dropdown-toggle-blue dropdown-toggle-right text-white">
                                Reports
                            </DropdownToggle>
                            <DropdownMenu>
                                <Link className="dropdown-item" to={`/reports/stats/affiliates?campaignId=${campaignId}&withAvatar=true&navigation=campaign${['AM_JUNIOR', 'AM_SENIOR', 'AM_JR_TEMLEAD'].includes(auth.user.role) ? `&adminId=${auth.user.id}` : ''}`}>
                                    Affiliate Earnings
                                </Link>
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/reports/stats/daily?campaignId=${campaignId}&withAvatar=true&navigation=campaign${['AM_JUNIOR', 'AM_SENIOR', 'AM_JR_TEMLEAD', 'MERMGR', 'ADOPS_MANAGER', 'ADOPS_TEAMLEAD', 'BIZDEV', 'BIZDEV_DIRECTOR'].includes(auth.user.role) ? `&adminId=${auth.user.id}` : ''}`}>
                                    Daily Earnings
                                </Link>
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/reports/stats/monthly?campaignId=${campaignId}&withAvatar=true&navigation=campaign&startDate=${moment().add(-12, 'months').startOf('month').format('YYYY-MM-DD')}&endDate=${moment().format('YYYY-MM-DD')}${['AM_JUNIOR', 'AM_SENIOR', 'AM_JR_TEMLEAD', 'MERMGR', 'ADOPS_MANAGER', 'ADOPS_TEAMLEAD', 'BIZDEV', 'BIZDEV_DIRECTOR'].includes(auth.user.role) ? `&adminId=${auth.user.id}` : ''}`}>
                                    Monthly Earnings
                                </Link>
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/reports/stats/conversions?campaignId=${campaignId}&navigation=campaign&startDate=${moment().startOf('day').format('YYYY-MM-DD')}${['AM_JUNIOR', 'AM_SENIOR', 'AM_JR_TEMLEAD', 'MERMGR', 'ADOPS_MANAGER', 'ADOPS_TEAMLEAD', 'BIZDEV', 'BIZDEV_DIRECTOR'].includes(auth.user.role) ? `&adminId=${auth.user.id}` : ''}`}>
                                    Conversions
                                </Link>
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/reports/custom/campaignPromiseReport?campaignId=${campaignId}&navigation=campaign`}>
                                    Promise to Run
                                </Link>
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/campaign/${campaignId}/serverPostbackCalls`}>
                                    Test Postbacks
                                </Link>
                                {dataLoaded && data.campaign.tracking.is_mobile_offer && (
                                    <>
                                        <DropdownItem divider className="m-0" />
                                        <Link className="dropdown-item" to={`/campaign/${campaignId}/kpiReport`}>
                                            KPI Report
                                        </Link>
                                    </>
                                )}
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/reports/campaignAffComparisonReport?campaignId=${campaignId}&navigation=campaign&startDate=${moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')}&endDate=${moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')}&startDate2=${moment().startOf('month').format('YYYY-MM-DD')}&endDate2=${moment().endOf('month').format('YYYY-MM-DD')}`}>
                                    Affiliate Comparison
                                </Link>
                            </DropdownMenu>

                        </Dropdown>
                    </li>
                    <li style={{ zIndex: 999 }}>
                        <Dropdown isOpen={trafficOpen} toggle={() => setTrafficOpen(!trafficOpen)} direction="down">
                            <DropdownToggle tag="a" data-toggle="dropdown" aria-expanded={trafficOpen} className="nav-link dropdown-toggle dropdown-toggle-blue dropdown-toggle-right text-white">
                                Traffic
                            </DropdownToggle>
                            <DropdownMenu>
                                <Link className="dropdown-item" to={`/campaign/${campaignId}/analytics`}>
                                    Analytics
                                </Link>
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/campaign/${campaignId}/clickReferrers?startDate=${moment().format('YYYY-MM-DD')}&endDate=${moment().format('YYYY-MM-DD')}`}>
                                    Click Referrers
                                </Link>
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/campaign/${campaignId}/leadReferrers?startDate=${moment().format('YYYY-MM-DD')}&endDate=${moment().format('YYYY-MM-DD')}`}>
                                    Lead Referrers
                                </Link>
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/campaign/${campaignId}/leadUserAgents`}>
                                    Lead User Agents
                                </Link>
                                <DropdownItem divider className="m-0" />
                                <Link className="dropdown-item" to={`/campaign/${campaignId}/leadLocations`}>
                                    Lead Locations
                                </Link>
                            </DropdownMenu>
                        </Dropdown>
                    </li>
                    <li>
                        <Dropdown isOpen={complianceOpen} toggle={() => setComplianceOpen(!complianceOpen)} direction="down">
                            <DropdownToggle tag="a" data-toggle="dropdown" aria-expanded={complianceOpen} className="nav-link dropdown-toggle dropdown-toggle-blue dropdown-toggle-right text-white">
                                Compliance
                            </DropdownToggle>
                            <DropdownMenu>
                                {data.campaign && !data.campaign.compliance.fraud_watch.enabled ? (
                                    <Link
                                        className="dropdown-item"
                                        to={`/`}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            setModalData({ isCreate: true, item: fraudWatch, isOpen: true })
                                        }}
                                    >
                                        Add to Fraud Watch
                                    </Link>
                                ) : (
                                    <Link
                                        className="dropdown-item"
                                        to={`/`}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            setModalData({ isCreate: false, item: data.campaign.compliance.fraud_watch.details, isOpen: true })
                                        }}
                                    >
                                        Edit Fraud Watch
                                    </Link>
                                )}
                            </DropdownMenu>
                        </Dropdown>
                    </li>
                    {(dataLoaded && data.campaign.tracking.is_mobile_offer) &&
                        <li>
                            <Dropdown isOpen={mobileOpen} toggle={() => setMobileOpen(!mobileOpen)} direction="down">
                                <DropdownToggle tag="a" data-toggle="dropdown" aria-expanded={mobileOpen} className="nav-link dropdown-toggle dropdown-toggle-blue dropdown-toggle-right text-white">
                                    Mobile
                                </DropdownToggle>
                                <DropdownMenu>
                                    <Link className="dropdown-item" to={`/campaign/${campaignId}/KPIReport`}>
                                        KPI Report
                                    </Link>
                                    <DropdownItem divider className="m-0" />
                                    <Link className="dropdown-item" to={`/campaigns/mobileParameters`}>
                                        Mobile Parameters
                                    </Link>
                                </DropdownMenu>
                            </Dropdown>
                        </li>
                    }
                </ul>
            </nav>
        </>
    )
}

export default CampaignNavbar
